/**
 * FRONT-581: map the real result of `POST /api/voice/realtime/ended` to the
 * message the user sees after a voice call ends.
 *
 * The old flow fired an optimistic "Call transcript kept" toast on hang-up,
 * before the server had done anything, and swallowed any failure — so the user
 * was told a recap was saved even when none was. This helper keys the message
 * off what actually happened, so the signal can never claim a success that
 * didn't occur.
 */
export interface EndedResult {
  ok?: boolean;
  recapPersisted?: boolean;
  proposalCreated?: boolean;
}

export interface RecapOutcome {
  kind: "success" | "info" | "error";
  title: string;
  description?: string;
}

/**
 * `result === null` means the request failed (network error or a non-2xx from
 * `/ended`) — the recap was NOT generated, so we say so instead of implying
 * success.
 */
export function describeVoiceRecapOutcome(result: EndedResult | null): RecapOutcome {
  if (!result || result.ok === false) {
    return {
      kind: "error",
      title: "Couldn't save the call recap",
      description: "The call ended but the recap failed to generate — nothing was saved. Please try again.",
    };
  }
  if (result.recapPersisted) {
    return {
      kind: "success",
      title: "Call recap saved to this chat",
      description: result.proposalCreated
        ? "A save card is below to approve what's worth remembering."
        : undefined,
    };
  }
  // Reached the server cleanly, but there was nothing meaningful to summarize.
  return {
    kind: "info",
    title: "Call ended",
    description: "Nothing worth recapping from this conversation.",
  };
}
